import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useHealth } from '@/hooks/useHealth'

function statusBarColor(status: 'healthy' | 'degraded' | 'down'): string {
  switch (status) {
    case 'healthy':  return 'bg-primary'
    case 'degraded': return 'bg-[#ffd080]'
    case 'down':     return 'bg-cmd-error'
  }
}

function statusLabel(status: 'healthy' | 'degraded' | 'down'): string {
  switch (status) {
    case 'healthy':  return 'ONLINE'
    case 'degraded': return 'DEGRADED'
    case 'down':     return 'OFFLINE'
  }
}

export function HealthMonitor() {
  const { data, isLoading, error } = useHealth()

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-cmd-error-container p-4">
          <p className="text-sm text-cmd-on-error font-label">FAULT DETECTED: {error.message}</p>
        </div>
      )}

      {/* Overall status */}
      <div className="grid gap-1 sm:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>OVERALL STATUS</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : data ? (
              <Badge
                variant={data.status === 'healthy' ? 'success' : 'destructive'}
                className="text-sm px-3 py-1"
              >
                {data.status === 'healthy' ? 'SYSTEM ONLINE' : 'FAULT DETECTED'}
              </Badge>
            ) : (
              <span className="font-label text-sm text-cmd-outline">NO SIGNAL</span>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>SERVICES</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-10 w-20" />
            ) : (
              <p className="font-mono text-[2rem] font-medium text-white tabular-nums">
                {data?.services?.filter((s) => s.status === 'healthy').length ?? 0}
                <span className="text-lg text-cmd-outline">/{data?.services?.length ?? 0}</span>
              </p>
            )}
            <p className="mt-1 font-label text-[0.6rem] text-cmd-outline uppercase">PASSING CHECKS</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>LAST SYNC</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-32" />
            ) : data?.checkedAt ? (
              <p className="font-mono text-sm font-medium text-white tabular-nums">
                {new Date(data.checkedAt).toLocaleTimeString()}
              </p>
            ) : (
              <span className="font-label text-sm text-cmd-outline">--</span>
            )}
            <p className="mt-1 font-label text-[0.6rem] text-cmd-outline uppercase">AUTO-REFRESH 30S</p>
          </CardContent>
        </Card>
      </div>

      {/* Service detail */}
      <Card>
        <CardHeader>
          <CardTitle>SERVICE STATUS</CardTitle>
        </CardHeader>
        <CardContent className="space-y-0">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between p-3">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-5 w-16" />
              </div>
            ))
          ) : data?.services?.length ? (
            data.services.map((svc, i) => (
              <div
                key={svc.name}
                className={`relative flex items-center justify-between p-3 ${i % 2 === 0 ? 'bg-cmd-surface' : 'bg-cmd-low'}`}
              >
                {/* Status bar */}
                <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${statusBarColor(svc.status)}`} />

                <div className="flex items-center gap-2 pl-3">
                  <span className="font-label text-xs text-white/80 uppercase">{svc.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  {svc.detail && (
                    <span className="font-mono text-[0.65rem] text-cmd-outline">{svc.detail}</span>
                  )}
                  <Badge variant={svc.status === 'healthy' ? 'success' : 'destructive'} className="text-[10px]">
                    {statusLabel(svc.status)}
                  </Badge>
                </div>
              </div>
            ))
          ) : (
            <p className="font-label text-sm text-cmd-outline text-center p-6">NO SIGNAL</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
